/**
 * Pure model of the Travail cockpit layout (refonte du 15/09/2026).
 *
 * The page used to stack every zone in one column — decisions, running missions, the
 * human to-do, the journal — « 50 mètres de long » with an empty detail panel beside
 * it. It is now a master/detail screen: tabs on the left, the selected thing on the
 * right. This module decides WHAT is listed and WHERE a click lands; components only
 * paint it. Both tab and selection live in the URL (principle 5), so a link to « the
 * Chauchard mission, waiting tab » is shareable and survives a reload.
 */
import type { AwaitingMission } from '../../hooks/useMissionsAwaitingHuman';
import type { OttoDraft } from '../../hooks/useOttoDrafts';
import type { WaitingItem } from '../../hooks/useWorkWaitingItems';
import { describeDoubts } from '../../../supabase/functions/_shared/autonomyDoubt';
import type { DeliverableFamily } from '../deliverables/registry';
import {
  contactLine,
  formatSlot,
  proposalKindLabel,
  proposalTitle,
  type AppointmentProposal,
} from '../appointments/proposalView';
import { questionFacts } from '../mission/questionFacts';
import { MISSION_FOCUS_PARAM } from '../mission/missionDecisionHref';
import type { ExchangeHeader } from './exchangeGroups';

/**
 * « Ta liste » (the human to-do) gave way to the flux on 30/09/2026 (PLAN-FLUX-ET-COUVERTURE
 * § 1bis: a mission IS the task); « Revue » is the Friday net of the flux (§ 2.5).
 */
export const COCKPIT_TABS = ['attente', 'en-cours', 'journal', 'revue'] as const;
export type CockpitTab = (typeof COCKPIT_TABS)[number];

/** Query parameters of the cockpit URL. `mission` is shared with the Relations strip. */
export const COCKPIT_PARAMS = {
  tab: 'vue',
  mission: MISSION_FOCUS_PARAM,
  proposal: 'rdv',
  draft: 'brouillon',
  report: 'cr',
  loop: 'boucle',
  competence: 'competence',
  procedure: 'procedure',
  loopSpec: 'fiche-boucle',
  autonomy: 'autonomie',
  review: 'relecture',
  learning: 'consigne',
  kbRevision: 'revision-doc',
  stripeProposal: 'stripe',
  kbDraft: 'article',
  linkedinDraft: 'post',
  rdv: 'rdv-passe',
  opportunity: 'maj-crm',
  inapp: 'message-in-app',
  chatReply: 'reponse-chat',
  mcpGesture: 'geste-session',
  linkedinGesture: 'geste-linkedin',
  loopChange: 'boucle-changement',
  qaCase: 'cas-de-test',
  /** « Nouvelle mission » (PLAN-BUREAU lot 5): the pane where Gilles hands Otto a subject. */
  newMission: 'nouvelle',
  /** A scheduled mail held at its time (PLAN-BUREAU lot 6): it comes back to « T'attend ». */
  heldMail: 'retenu',
} as const;

/** Target kinds reached by a single id in the query string (the route item has two parts). */
const ID_TARGETS = [
  'proposal', 'draft', 'report', 'loop', 'competence', 'procedure', 'loopSpec', 'autonomy', 'review', 'learning',
  'kbRevision', 'stripeProposal', 'kbDraft', 'linkedinDraft', 'rdv', 'opportunity', 'inapp', 'linkedinGesture',
  'loopChange', 'qaCase', 'heldMail', 'chatReply', 'mcpGesture',
] as const;
type IdTargetKind = (typeof ID_TARGETS)[number];

export function parseCockpitTab(value: string | null | undefined): CockpitTab {
  return (COCKPIT_TABS as readonly string[]).includes(value ?? '') ? (value as CockpitTab) : 'attente';
}

/** What the detail pane shows. A mission always wraps a work item: `item` is the usual case. */
export type CockpitTarget =
  | { kind: 'item'; source: string; sourceId: string }
  | { kind: 'mission'; threadId: string }
  | { kind: 'new' }
  | { kind: IdTargetKind; id: string };

/** Reads the selection from the route params and the query string; the route item wins. */
export function targetFromLocation(
  route: { source?: string; sourceId?: string },
  search: URLSearchParams,
): CockpitTarget | null {
  if (route.source && route.sourceId) return { kind: 'item', source: route.source, sourceId: route.sourceId };
  const mission = search.get(COCKPIT_PARAMS.mission);
  if (mission) return { kind: 'mission', threadId: mission };
  if (search.get(COCKPIT_PARAMS.newMission)) return { kind: 'new' };
  for (const kind of ID_TARGETS) {
    const id = search.get(COCKPIT_PARAMS[kind]);
    if (id) return { kind, id };
  }
  return null;
}

/** Stable identity of a target, used to highlight the selected row. */
export function targetKey(target: CockpitTarget | null): string | null {
  if (!target) return null;
  switch (target.kind) {
    case 'item':
      return `item:${target.source}/${target.sourceId}`;
    case 'mission':
      return `mission:${target.threadId}`;
    case 'new':
      return 'new';
    default:
      return `${target.kind}:${target.id}`;
  }
}

/**
 * The URL of a tab with (optionally) a selection. The default tab is left out of the URL.
 * `keep` carries the query parameters that must survive a selection — the list view
 * (search, filter, grouping, sort: `listView`).
 */
export function cockpitHref(tab: CockpitTab, target: CockpitTarget | null, keep?: URLSearchParams): string {
  const params = new URLSearchParams(keep);
  if (tab !== 'attente') params.set(COCKPIT_PARAMS.tab, tab);
  let path = '/admin/travail';
  if (target?.kind === 'item') {
    path = `/admin/travail/${encodeURIComponent(target.source)}/${encodeURIComponent(target.sourceId)}`;
  } else if (target?.kind === 'mission') {
    params.set(COCKPIT_PARAMS.mission, target.threadId);
  } else if (target?.kind === 'new') {
    params.set(COCKPIT_PARAMS.newMission, '1');
  } else if (target) {
    params.set(COCKPIT_PARAMS[target.kind], target.id);
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

interface SourceRef {
  source: string;
  source_id: string;
}

const itemOf = (ref: SourceRef | undefined): CockpitTarget | null =>
  ref ? { kind: 'item', source: ref.source, sourceId: ref.source_id } : null;

/**
 * What the detail pane opens when a tab is shown with nothing selected (desktop only):
 * its first line, so the pane is never blank. The journal is a feed, not a queue — it
 * opens nothing by itself, nor does the review (a net to go through, not a queue).
 * « T'attend » opens the first line of « Maintenant »; « Otto travaille » starts with its
 * halts, the lines with a gesture (« Relancer »).
 */
export function firstTargetOfTab(
  tab: CockpitTab,
  lists: {
    waiting: readonly WaitingEntry[];
    halted: readonly SourceRef[];
    running: readonly SourceRef[];
  },
): CockpitTarget | null {
  switch (tab) {
    case 'attente':
      return lists.waiting[0]?.target ?? null;
    case 'en-cours':
      return itemOf(lists.halted[0] ?? lists.running[0]);
    case 'journal':
    case 'revue':
      return null;
  }
}

/** The line kinds of the server reader `get_work_waiting_items` (`ITEM_KIND`). */
export type ItemKind =
  | 'report' | 'loop' | 'competence' | 'procedure' | 'loopSpec' | 'autonomy' | 'review' | 'learning'
  | 'kbRevision' | 'stripeProposal' | 'kbDraft' | 'linkedinDraft' | 'rdv' | 'opportunity' | 'inapp' | 'linkedinGesture'
  | 'loopChange' | 'qaCase' | 'chatReply' | 'mcpGesture';

/**
 * One line of « T'attend »: something only Gilles can move — or, kind `exchange`, a card: everything
 * one exchange (meeting or call) or one mission left him, gathered as ONE element (PLAN-SUITES-ECHANGES lot 6).
 */
export interface WaitingEntry {
  key: string;
  kind: 'mission' | 'proposal' | 'draft' | ItemKind | 'exchange';
  title: string;
  /** What is asked of him, in a few words. */
  hint: string;
  /** Since when it waits — the list is ordered oldest first. */
  since: string | null;
  target: CockpitTarget;
  /** The mission behind the line, when there is one — its client is read from it. */
  threadId: string | null;
  /** The client carried by the line itself (an appointment proposal knows its account). */
  account: { id: string; name: string } | null;
  /** A mission waiting on a CALL from Gilles, not a written answer (`ask_kind = 'call'`). */
  asksCall: boolean;
  /** A mission waiting on a DELIVERABLE to validate (`ask_kind = 'deliverable'`). */
  asksDeliverable: boolean;
  /** The mail draft the line is, for a draft's line. */
  draftId: string | null;
  /** The competence the line carries itself (server reader); a mission's comes from the mission. */
  competence: { slug: string; label: string } | null;
  /**
   * The key of the mission whose question this line carries (a draft its question is about,
   * `about_draft_id`): what Gilles decided on the mission (report, lane) passes to the line.
   */
  carries?: string | null;
  /** A card's header (null until read) and its lines (`groupByExchange`); absent on a line. */
  group?: { exchange: ExchangeHeader | null; members: WaitingEntry[] };
}

/**
 * The family of a line (PLAN-LEARNING-LOOPS § 6.2): what kind of deliverable it is, which
 * viewer opens it and how its main gesture is named (`deliverables/registry`). A call to make
 * is RDV / appel, not a question: it is answered with the phone (asked on 22/09/2026).
 */
export function waitingFamily(entry: Pick<WaitingEntry, 'kind' | 'asksCall' | 'asksDeliverable'>): DeliverableFamily {
  switch (entry.kind) {
    case 'draft':
      return 'message';
    case 'proposal':
    case 'rdv':
      return 'rdv';
    case 'report':
      return 'cr';
    case 'loop':
    case 'loopSpec':
    case 'loopChange':
      return 'boucle';
    case 'competence':
    case 'procedure':
    case 'autonomy':
    case 'learning':
      return 'memoire';
    case 'review':
    case 'chatReply': // a reply proposed in an in-app conversation (21/10/2026): a message to send
      return 'message';
    case 'kbRevision':
    case 'kbDraft':
    case 'linkedinDraft':
    case 'mcpGesture': // a gesture of a person's MCP session (22/10/2026): something to put online, or not
      return 'contenu';
    case 'stripeProposal':
      return 'facturation';
    case 'opportunity':
      return 'crm';
    case 'inapp':
      return 'inapp';
    case 'linkedinGesture':
      return 'linkedin';
    case 'qaCase':
      return 'qualite';
    case 'exchange':
      return 'echange';
    case 'mission':
      return entry.asksCall ? 'rdv' : entry.asksDeliverable ? 'atelier' : 'question';
  }
}

function missionHint(mission: AwaitingMission, timeZone: string): string {
  const base = mission.status === 'proposed'
    ? 'Plan à valider'
    : mission.status === 'awaiting_validation'
    ? 'Résultat à relire'
    : mission.ask_kind === 'call'
    ? 'Appel à passer'
    : mission.ask_kind === 'deliverable'
    ? 'Livrable à valider'
    : mission.ask_kind === 'screen'
    ? "Geste à l'écran"
    : mission.ask_kind === 'workshop'
    ? 'Atelier à valider'
    : 'Question pour toi';
  // Ce qui a bougé sous la question : ce qu'Otto en fait (il la relit) quand il la relira, sinon
  // le fait lui-même — la rupture d'une prémisse déclarée avant le compteur d'événements
  // (questionFacts, la même définition que le volet de la mission).
  const facts = questionFacts(mission, timeZone);
  if (facts?.kind === 'rereading') return `${base} · ${facts.short}`;
  if (facts?.kind === 'facts') return `${base} · ${facts.text}`;
  return base;
}

/** The line kind of each family of the server reader (the `kind` of its `WaitingEntry.key`). */
export const ITEM_KIND: Record<WaitingItem['family'], ItemKind> = {
  cr: 'report',
  loop_report: 'loop',
  competence: 'competence',
  procedure: 'procedure',
  loop_spec: 'loopSpec',
  autonomy_open: 'autonomy',
  autonomy_review: 'review',
  learning: 'learning',
  kb_revision: 'kbRevision',
  stripe_proposal: 'stripeProposal',
  kb_draft: 'kbDraft',
  linkedin_draft: 'linkedinDraft',
  rdv: 'rdv',
  // Same word on both sides: flux_waiting_keys keeps the family as the key's kind.
  opportunity: 'opportunity',
  inapp: 'inapp',
  linkedinGesture: 'linkedinGesture',
  loopChange: 'loopChange',
  qaCase: 'qaCase',
  chatReply: 'chatReply',
  mcpGesture: 'mcpGesture',
};

/** What a draft's line says: its autonomous send (countdown or doubt, P6) before the usual. */
export function draftHint(
  d: Pick<OttoDraft, 'recipient' | 'autonomy'> & { revision?: OttoDraft['revision'] },
  now: Date = new Date()): string {
  if (d.revision === 'untreated') return 'Ton retour n’a pas été repris par Otto';
  const base = d.recipient ? `Brouillon à relire → ${d.recipient}` : 'Brouillon à relire';
  const a = d.autonomy;
  if (!a) return base;
  if (a.status === 'held') return `Otto doute : ${describeDoubts(a.reasons)}`;
  if (a.status === 'sending') return 'Part seul en ce moment';
  const minutes = a.sendAfter ? Math.max(0, Math.ceil((new Date(a.sendAfter).getTime() - now.getTime()) / 60_000)) : null;
  return minutes === null ? 'Part seul bientôt · annulable' : `Part seul dans ${minutes} min · annulable`;
}

/**
 * « T'attend », in one list (PLAN-LEARNING-LOOPS § 6.1): the missions stopped on a decision,
 * the appointments Otto proposed, his mail drafts, and — from the server reader
 * `get_work_waiting_items` — the CRs to send, the loops' proposals and the proposed changes of
 * the competence catalogue (the actions to validate left with the Actions queue, 06/10/2026).
 *
 * WHICH lines wait is decided by the server alone (`flux_waiting_keys()`, read through the live
 * signal `flux_waiting_state`): a mission paused on a proposal appears once, as the proposal; a CR
 * mission once, as its CR; a draft Gilles sent back to Otto does not wait while the mission
 * rewrites it; every other draft is a line of its own (01/10/2026 — a draft folded into its
 * mission's line went unseen), gathered with its mission's other lines in one card
 * (`groupByExchange`). This function only paints those keys from the readers' rows.
 * Oldest first: what has waited longest deserves the first look.
 */
export function buildWaitingEntries(input: {
  missions: readonly AwaitingMission[];
  drafts: readonly OttoDraft[];
  proposals: readonly AppointmentProposal[];
  items?: readonly WaitingItem[];
  /** The keys that wait (`flux_waiting_state().keys`); nothing waits until they are known. */
  keys: ReadonlySet<string>;
  /** The company's zone: the dates a hint names (`useOrganizationTimeZone()`). */
  timeZone: string;
}): WaitingEntry[] {
  const waits = (key: string) => input.keys.has(key);
  const missionByThread = new Map(input.missions.map((m) => [m.thread_id, m]));
  // A question about a draft is carried by that draft: the server lists no mission line for it.
  const askerOf = new Map(
    input.missions.flatMap((m) => (m.about_draft_id ? [[m.about_draft_id, m.thread_id] as const] : [])),
  );

  const entries: WaitingEntry[] = [
    ...input.missions
      .filter((m) => waits(`mission:${m.thread_id}`))
      .map<WaitingEntry>((m) => ({
        key: `mission:${m.thread_id}`,
        kind: 'mission',
        title: m.mission_title,
        hint: missionHint(m, input.timeZone),
        since: m.waiting_since,
        target: { kind: 'item', source: m.source, sourceId: m.source_id },
        threadId: m.thread_id,
        account: null,
        asksCall: m.ask_kind === 'call',
        asksDeliverable: m.ask_kind === 'deliverable',
        draftId: null,
        competence: null,
      })),
    ...input.proposals.filter((p) => waits(`proposal:${p.id}`)).map<WaitingEntry>((p) => {
      const mission = p.mission_thread_id ? missionByThread.get(p.mission_thread_id) : undefined;
      return {
        key: `proposal:${p.id}`,
        kind: 'proposal',
        title: mission?.mission_title ?? `${proposalTitle(p)} · ${contactLine(p)}`,
        hint: `${proposalKindLabel(p.kind)} · ${formatSlot(p.start_time, p.end_time, input.timeZone)}`,
        since: p.created_at,
        target: mission
          ? { kind: 'item', source: mission.source, sourceId: mission.source_id }
          : { kind: 'proposal', id: p.id },
        threadId: p.mission_thread_id,
        account: p.account_id && p.account_name ? { id: p.account_id, name: p.account_name } : null,
        asksCall: false,
        asksDeliverable: false,
        draftId: null,
        competence: null,
      };
    }),
    ...input.drafts
      .filter((d) => waits(`draft:${d.id}`))
      .map<WaitingEntry>((d) => ({
        key: `draft:${d.id}`,
        kind: 'draft',
        title: d.subject || d.preview || '(sans objet)',
        hint: askerOf.has(d.id) ? `Otto te pose une question · ${draftHint(d)}` : draftHint(d),
        carries: askerOf.has(d.id) ? `mission:${askerOf.get(d.id)}` : null,
        since: d.createdAt,
        target: { kind: 'draft', id: d.id },
        threadId: d.missionThreadId,
        account: null,
        asksCall: false,
        asksDeliverable: false,
        draftId: d.id,
        competence: null,
      })),
    ...(input.items ?? [])
      .filter((item) => waits(`${ITEM_KIND[item.family]}:${item.item_id}`))
      .map<WaitingEntry>((item) => {
        const kind = ITEM_KIND[item.family];
        return {
          key: `${kind}:${item.item_id}`,
          kind,
          title: item.title,
          hint: item.hint,
          since: item.since,
          target: { kind, id: item.item_id },
          threadId: item.thread_id,
          account: item.account_id && item.account_name ? { id: item.account_id, name: item.account_name } : null,
          asksCall: false,
          asksDeliverable: false,
          draftId: null,
          competence: item.competence_slug && item.competence_label
            ? { slug: item.competence_slug, label: item.competence_label }
            : null,
        };
      }),
  ];

  const time = (iso: string | null) => {
    const ms = iso ? new Date(iso).getTime() : Number.NaN;
    return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
  };
  return entries.sort((a, b) => time(a.since) - time(b.since));
}

const SUMMARY_WORDS: Record<DeliverableFamily, [string, string]> = {
  message: ['brouillon à envoyer', 'brouillons à envoyer'],
  cr: ['CR à envoyer', 'CR à envoyer'],
  rdv: ['RDV ou appel', 'RDV ou appels'],
  question: ['question', 'questions'],
  atelier: ['livrable à valider', 'livrables à valider'],
  boucle: ['proposition de boucle', 'propositions de boucles'],
  memoire: ['décision de mémoire', 'décisions de mémoire'],
  contenu: ['contenu à relire', 'contenus à relire'],
  facturation: ['écriture Stripe', 'écritures Stripe'],
  crm: ['mise à jour CRM', 'mises à jour CRM'],
  inapp: ['message in-app à retirer', 'messages in-app à retirer'],
  linkedin: ['geste LinkedIn', 'gestes LinkedIn'],
  qualite: ['cas de test à trancher', 'cas de test à trancher'],
  echange: ['échange à suivre', 'échanges à suivre'],
};
const SUMMARY_ORDER: readonly DeliverableFamily[] = [
  'echange', 'message', 'cr', 'rdv', 'question', 'atelier', 'contenu', 'facturation', 'crm', 'inapp', 'linkedin', 'qualite',
  'boucle', 'memoire',
];

/** « 2 brouillons à envoyer · 1 CR à envoyer · 3 questions », in the order they deserve. */
export function waitingSummaryText(entries: readonly Pick<WaitingEntry, 'kind' | 'asksCall' | 'asksDeliverable'>[]): string {
  const counts = new Map<DeliverableFamily, number>();
  for (const e of entries) counts.set(waitingFamily(e), (counts.get(waitingFamily(e)) ?? 0) + 1);
  return SUMMARY_ORDER.flatMap((family) => {
    const n = counts.get(family) ?? 0;
    if (n === 0) return [];
    const [one, many] = SUMMARY_WORDS[family];
    return [`${n} ${n === 1 ? one : many}`];
  }).join(' · ');
}
